import {useIntl} from 'react-intl';
import {useRouter} from 'next/router';
import {Flex, Text, Image, Center, Icon, Box, Circle, GridProps, Grid} from '@chakra-ui/react';

import {px} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {useRegionSettings} from '@eksab/hooks';
import {CoinsIcon} from '@eksab/assets/icons';

import {QuestionPoints, PrizeIcon, JoinChallengeModal} from './';
import {XIcon} from '../icons/XIcon';
import gamePadIcon from '../icons/gamepad.png';
import {useSetChallengeQueryData} from '../hooks';
import {useTriviaStore} from '../state';
import {getScoresPicture, getScoreStatus} from './utils';
import {GoogleBannerAd} from '@eksab/components';

export function Loser(props: GridProps) {
  const t = useTranslate();
  const router = useRouter();
  const format = useIntl().formatNumber;

  const setChallengeQueryData = useSetChallengeQueryData();
  const {challenge, currentScore, currentQuestion, wrongAnswer} = useTriviaStore();

  const isWE = challenge?.game === 'trivia-misc-we';

  const scoreStatus = getScoreStatus(currentScore, challenge!.no_of_questions);

  const openJoinChallengeModal = () => {
    mixpanel?.track('Button Clicked', {button: 'Trivia - Retry'});

    setChallengeQueryData(challenge!);
    router.push({query: {...router.query, 'challenge-id': challenge?.id}}, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  return (
    <Grid
      flex={1}
      px={px}
      w="fit-content"
      templateColumns={{base: '1fr', lg: '1fr 1fr'}}
      columnGap="16"
      rowGap={{base: '2', lg: '4'}}
      placeItems="center"
      alignContent={{base: 'start', lg: 'center'}}
      textAlign="center"
      {...props}
    >
      <Image
        src={getScoresPicture(challenge!.topic, currentScore, challenge?.no_of_questions || 0)}
        alt=""
        gridRow={{base: 'auto', lg: '1 / 5'}}
        w={[48, null, null, 96]}
        h="auto"
      />

      {!isWE && (
        <Box gridRow={{base: '6 / span 1', lg: '5 / span 1'}}>
          <GoogleBannerAd size="narrow" slotId="gpt-ad-trivia-loser" />
        </Box>
      )}

      {!isWE && <QuestionPoints gridRow="1" />}

      <Flex gap="1" flexDir="column" align="center" textAlign="center" maxW="2xl">
        <Text fontSize={['22px', null, null, '34px']} fontWeight="800" color="#131313">
          {!isWE ? t(`trivia.score.${challenge!.topic}.${scoreStatus}.heading`) : t(`trivia.score.we.heading.0-4`)}
        </Text>

        {!isWE && (
          <Text fontSize="xs" fontWeight="600" color="#8f8f8f">
            {t(`trivia.score.${challenge!.topic}.${scoreStatus}.subheading`)}
          </Text>
        )}
      </Flex>

      {/* Question and Answer */}
      {challenge?.termination_mode == 'stop_on_wrong_answer' && !!wrongAnswer.length && (
        <Flex bgColor="#F3F3F3" rounded="20px" py="4" ps="6" pe="10" gap="4" alignItems="center">
          {currentQuestion?.type === 'image' && (
            <Image
              flexShrink={0}
              width="12"
              height="12"
              rounded="full"
              objectFit="cover"
              objectPosition="center"
              src={currentQuestion?.image}
              alt=""
            />
          )}

          <Box textAlign="start">
            <Text fontWeight="bold" mb="0.5">
              {format(currentScore)}
              <Text as="span" fontSize="10" fontWeight="normal" lineHeight="1" color="#9B9B9B">
                {' '}
                /{' '}
              </Text>
              <Text as="span" fontSize="xs" color="#9B9B9B">
                {format(challenge?.no_of_questions || 0)}{' '}
              </Text>
            </Text>
            <Text fontSize="xs" fontWeight="semibold" mb="2">
              {currentQuestion?.body}
            </Text>
            <Flex p="1" bgColor="#E3E3E3" rounded="full" gap="3">
              <Circle size="5" bgColor="white" justifyContent="center" alignItems="center">
                <XIcon />
              </Circle>
              <Text fontWeight="medium" fontSize="xs" fontStyle="italic" color="#646464">
                {wrongAnswer[0]?.body}
              </Text>
            </Flex>
          </Box>
        </Flex>
      )}

      {!isWE ? (
        <RetryButton
          prize={challenge?.prize as Prize}
          entryFee={challenge?.entry_fee}
          onClick={openJoinChallengeModal}
        />
      ) : (
        <PlayMoreChallengesButton
          onClick={() => {
            mixpanel?.track('Button Clicked', {button: 'Trivia WE - Play More Challenges'});
            router.push('/play/trivia-we');
          }}
        />
      )}

      <JoinChallengeModal />
    </Grid>
  );
}

const RetryButton = ({prize, entryFee, onClick}: {prize: Prize; entryFee: number | undefined; onClick: () => void}) => {
  const format = useIntl().formatNumber;
  const challenge = useTriviaStore((state) => state.challenge!);

  const t = useTranslate();

  const {data: region} = useRegionSettings();

  return (
    <Flex flexDir="column" align="center" textAlign="center" maxW="4xl" cursor="pointer" onClick={onClick}>
      <Flex flexDir="row" px="4" py="4" align="center" w="full" gap="4" justify="center" bg="#5AC57B" rounded="44px">
        <Flex py="0.5" px="2" rounded="60px" bg="#4FA86A" gap="2">
          <CoinsIcon width="15px" height="auto" />
          <Flex gap="1">
            <Text color="white" fontSize="sm" fontWeight="700">
              {!!entryFee ? format(entryFee) : t('trivia.challenge.no-fees')}
            </Text>
            {!!entryFee && (
              <Text color="#FED44C" fontSize="sm" fontWeight="700" whiteSpace="nowrap">
                ₡ {t('competition.overview.fee')}
              </Text>
            )}
          </Flex>
        </Flex>
        <Text color="white" fontSize="md" fontWeight="800">
          {t(`trivia.score.${challenge.topic}.retry`)}
        </Text>
      </Flex>

      <Center as="button" pos="relative" px="6">
        <Icon
          pos="absolute"
          inset="0"
          zIndex="-1"
          boxSize="full"
          viewBox="0 0 260 43"
          preserveAspectRatio="none"
          px="inherit"
        >
          <path
            d="M0 0H254L247.237 24.4087C244.356 34.8037 234.895 42 224.108 42H30.8073C20.3222 42 11.0507 35.1934 7.90959 25.1899L0 0Z"
            fill="#131313"
          />
        </Icon>

        <Flex py="1.5" align="center" gap="3" px="8" textAlign="center" fontSize="18px" fontWeight="700">
          {!!prize ? (
            <PrizeIcon width="32px" height="auto" type={prize?.type} />
          ) : (
            <Image src={gamePadIcon.src} alt="" objectFit="contain" width="32px" height="auto" />
          )}

          <Flex gap="1">
            {!!prize ? (
              <>
                <Text align="center" textColor="white">
                  {prize?.type === 'physical' ? prize?.name : format(prize?.amount || 0)}
                </Text>
                <Text align="center" textColor="#FED44C" fontWeight="600">
                  {prize.type !== 'physical' && (prize.type === 'cash' ? region?.currency : t('app.coins'))}{' '}
                  {t('trivia.prize.prize')}
                </Text>
              </>
            ) : (
              <Text align="center" textColor="white">
                {t('trivia.challenge.free-trial')}
              </Text>
            )}
          </Flex>
        </Flex>
      </Center>
    </Flex>
  );
};

const PlayMoreChallengesButton = ({onClick}: {onClick: () => void}) => {
  const t = useTranslate();

  return (
    <Flex flexDir="column" align="center" textAlign="center" maxW="4xl" cursor="pointer" onClick={onClick}>
      <Flex flexDir="row" px="4" py="4" align="center" w="full" gap="4" justify="center" bg="#5AC57B" rounded="44px">
        <Text color="white" fontSize="md" fontWeight="800">
          {t(`trivia.score.we.play-more-challenges`)}
        </Text>
      </Flex>
    </Flex>
  );
};
